import React, { forwardRef, ForwardedRef, ReactNode } from 'react';
import clsx from 'clsx';
import { AriaOverlayProps } from 'react-aria/useOverlay';
import { AriaDialogProps } from '@react-types/dialog';
import styles from '@danske/sapphire-css/components/panel/panel.module.css';

import { Modal, ModalLayoutContext } from '../..';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export type PanelProps = AriaOverlayProps &
  AriaDialogProps &
  SapphireStyleProps &
  GlobalDomAttributes & {
    /**
     * The content of the panel. Typically, a {@link ModalLayout} element.
     */
    children?: ReactNode;
    /**
     * @default 'lg'
     */
    size?: 'lg' | 'sm';

    /**
     * Callback for when the modal exit transition is done and not visible
     * anymore. This happens a short while after the `isOpen` state is set to
     * `false`.
     */
    onCloseTransitionDone?: () => void;

    /**
     * Whether the panel should be left-aligned relative to the window. This
     * should be used rarely and only when the trigger is on the left side of
     * the window (eg. for Sidebars)
     * @default false
     */
    alignLeft?: boolean;

    /**
     * Whether the panel is currently open.
     */
    isOpen: boolean;
  };

export const Panel = forwardRef(function Panel(
  props: PanelProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const { onClose, children, size = 'lg', alignLeft = false } = props;

  const { styleProps } = useSapphireStyleProps(props);

  return (
    <Modal
      {...props}
      // TODO: improve Backdrop to support more align options
      backdropProps={{
        style: { justifyContent: alignLeft ? 'flex-start' : 'flex-end' },
      }}
      ref={ref}
    >
      {({ titleProps, transitionState }) => (
        <div
          className={clsx(styleProps.className, styles['sapphire-panel'], {
            [styles['sapphire-panel--visible']]:
              transitionState === 'entering' || transitionState === 'entered',
            [styles['sapphire-panel--sm']]: size === 'sm',
            [styles['sapphire-panel--left']]: alignLeft,
          })}
          style={styleProps.style}
        >
          <ModalLayoutContext.Provider value={{ onClose, titleProps }}>
            {children}
          </ModalLayoutContext.Provider>
        </div>
      )}
    </Modal>
  );
});
